import {
  getLsrDocument,
  getLsrDocumentTypes,
  getLsrOrganization,
} from "@app/API";
import { useAsync } from "@app/utils/react-async-hook";
import { Document } from "../document";
import { useLegalSifterReview } from "./hooks";

export enum SiftStatus {
  Sifting = "SIFTING",
  Sifted = "SIFTED",
  Queued = "QUEUED",
  Uploading = "UPLOADING",
  Failed = "FAILED",
  None = "NONE",
  Converting = "CONVERTING",
  NotSupported = "NOT SUPPORTED",
  Canceled = "CANCELED",
}

const SIFT_STATUS_MAP: Record<string, SiftStatus> = Object.fromEntries(
  Object.entries({
    // LSC API Documents
    Queued: SiftStatus.Queued,
    Canceled: SiftStatus.Canceled,
    // LSR English & LSC API Documents
    Sifting: SiftStatus.Sifting,
    Sifted: SiftStatus.Sifted,
    Failed: SiftStatus.Failed,
    // LSR English
    Uploading: SiftStatus.Uploading,
    Converting: SiftStatus.Converting,
    "Not Supported": SiftStatus.NotSupported,
    // LSR Spanish
    Examinando: SiftStatus.Sifting,
    Examinado: SiftStatus.Sifted,
    Cargando: SiftStatus.Uploading,
    Fallido: SiftStatus.Failed,
    Convirtiendo: SiftStatus.Converting,
    "No es Apoyado": SiftStatus.NotSupported,
    // LSR Croatian
    Siftanje: SiftStatus.Sifting,
    Siftano: SiftStatus.Sifted,
    Učitavanje: SiftStatus.Uploading,
    Neuspješno: SiftStatus.Failed,
    Pretvaranje: SiftStatus.Converting,
    "Nije podržano": SiftStatus.NotSupported,
    // LSR Chinese
    正在审查: SiftStatus.Sifting,
    审查时间: SiftStatus.Sifted,
    正在上传: SiftStatus.Uploading,
    失败: SiftStatus.Failed,
    正在转换: SiftStatus.Converting,
    "Not 不支持": SiftStatus.NotSupported,
  }).map(([key, value]) => [key.toUpperCase(), value])
);

export interface LsrDocumentState {
  status: SiftStatus;
  documentTypeUuid: string;
  uuid: string;
  document?: LsrDocument;
}

export interface LsrDocument {
  "sifted-document": string;
  name: string;
  "current-export": string;
  "sifted-time": string;
  "document-type": string;
  edited: boolean;
  "external-id": string;
  "created-by": string;
  status: string;
  notes: string;
  "org-uuid": string;
  uuid: string;
  "original-document": string;
}

export interface LsrOrganization {
  "org-uuid": string;
  name: string;
  "sifter-settings": string;
  "sifter-trainer-report": string;
  "all-sifts-report": string;
}

export enum LsrUserRole {
  DocumentSifter = "document-sifter",
  SifterSettingsAdmin = "sifter-settings-admin",
  ClientAdmin = "client-admin",
}

export interface LsrUser {
  email: string;
  name: string;
  initials: string;
  active: boolean;
  roles: LsrUserRole[];
  "job-function": string;
  "org-uuid": string;
}

export function getLsrState(document: Document): LsrDocumentState | null {
  return document?.lsr as LsrDocumentState;
}

export function getSiftStatusFromString(lsrStatus: string): SiftStatus {
  return SIFT_STATUS_MAP[lsrStatus?.toUpperCase()] ?? SiftStatus.None;
}

export function getSiftStatusFromDocument(document: Document): SiftStatus {
  return getSiftStatusFromString(getLsrState(document)?.status);
}

export function isQueuedForSifting(document: Document): boolean {
  return getSiftStatusFromDocument(document) === SiftStatus.Queued;
}

export function isUploadingForSifting(document: Document): boolean {
  return getSiftStatusFromDocument(document) === SiftStatus.Uploading;
}

export function isConvertingForSifting(document: Document): boolean {
  return getSiftStatusFromDocument(document) === SiftStatus.Converting;
}

export function getLsrPlaybookId(document: Document): string {
  return getLsrState(document)?.documentTypeUuid ?? "";
}

export function getLsrDocumentId(document: Document): string {
  return getLsrState(document)?.uuid ?? "";
}

export function isSifting(document: Document): boolean {
  return (
    getSiftStatusFromDocument(document) === SiftStatus.Sifting ||
    isQueuedForSifting(document) ||
    isUploadingForSifting(document) ||
    isConvertingForSifting(document)
  );
}

export function isSifted(document: Document): boolean {
  return getSiftStatusFromDocument(document) === SiftStatus.Sifted;
}

export function getSiftedDocumentUrl(document: Document): string {
  return getLsrState(document)?.document?.["sifted-document"] || "";
}

export function getSiftedDocumentExportUrl(document: Document): string {
  return getLsrState(document)?.document?.["current-export"] || "";
}

export function useLsrDocument(lsrDocumentUuid): any {
  const asyncResponse = useAsync<any>(getLsrDocument, [lsrDocumentUuid], {
    setLoading: (state) => {
      return { ...state, loading: true, status: "loading" } as any; // keep previous results while loading new results
    },
  });

  return asyncResponse;
}

export function useLsrDocumentTypes(): any {
  const { result: documentTypes, status } = useAsync<any>(
    getLsrDocumentTypes,
    []
  );
  return [documentTypes, status];
}

export function getSifterSettingsUrl(lsrOrganization: LsrOrganization): string {
  return lsrOrganization?.["sifter-settings"] || "";
}

export function getSifterTrainerReportUrl(
  lsrOrganization: LsrOrganization
): string {
  return lsrOrganization?.["sifter-trainer-report"] || "";
}

export function getAllSiftsReportUrl(lsrOrganization: LsrOrganization): string {
  return lsrOrganization?.["all-sifts-report"] || "";
}

export function useLsrOrganization(): any {
  const [, lsrOrganizationId] = useLegalSifterReview();
  const { result: lsrOrganization, status } = useAsync<any>(
    getLsrOrganization,
    [lsrOrganizationId]
  );
  return [lsrOrganization, status];
}
